import * as React from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "../../windowManager/browserFenster/fensterPortal";
import {
    graphMailService,
    type Email,
    type MailFolder,
} from "../../../services/graphMailService";
import {
    mailLinkService,
    type MailLinkBatchItemInput,
} from "../../../services/mailLinkService";
import { SandboxedMailBody } from "../../mail/SandboxedMailBody";
import { MailSearchInput } from "../../mail/MailSearchInput";
import { canReadMailbox } from "../../../utils/mailboxAccess";
import { useMailContext } from "../../../context/MailContext";
import { useDebouncedValue } from "../../../utils/useDebouncedValue";
import {
    buildFolderNameMap,
    collectInboxFolderIds,
    mailboxChipLabel,
} from "../../mail/mailSearch";
import { buildParticipantsQuery, mergeThreadHits, type SidebarThreadHit } from "../../mail/mailContactHistory";
import { mailIdentitaet } from "./vorgangMails";
import "./vorgangDetail.css";
import { hinweisen } from "../../redesign/bestaetigen";
import { alleDokumente } from "../../windowManager/browserFenster/fensterRegister";

/**
 * Vg-SP5 — Redesign des Vorgang-/Todo-Mail-Pickers.
 *
 * Zonen: Kopfzeile · Filterzeile (Postfach + Ordner) · Suchfeld · zweispaltig
 * (Liste links, Vorschau rechts mit eigenem Panel-Hintergrund) · Sticky-Footer
 * (Archiv-Checkbox links, Buttons rechts).
 *
 * Zwei Modi:
 *  - BROWSE (kein aktiver Suchbegriff): Nachrichten des gewählten Postfach-/
 *    Ordners, paginiert („Mehr laden" über `fetchFolderMessagesPage`).
 *  - SUCHE (Suchbegriff ODER aktiver Kontakt-Vorschlag): debounced Fan-out über
 *    ALLE lesbaren Postfächer (`Promise.allSettled`), Merge + Sortierung nach
 *    Empfangsdatum absteigend. Jeder Treffer trägt Postfach- UND Ordner-Chip.
 *
 * Der KERN ist das {mailbox, messageId}-Paar-Modell: Vorschau, „Ganzer Verlauf"
 * (postfach-scoped!), Auswahl, Submit und die Cache-Invalidierung kennen die
 * QUELL-Mailbox je Nachricht — es gibt keine implizite „aktuelle Mailbox" mehr.
 * Bestätigen legt je Auswahl einen MailLink an (Batch) und archiviert die im
 * Posteingang liegenden Nachrichten (falls die Option aktiv ist).
 */

type Props = {
    vorgangId?: number;
    todoId?: number;
    /** Verknüpfter Kontakt — Quelle des Default-Suchvorschlags (participants:). */
    contactId?: number | null;
    /** Bereits zugewiesene messageIds — werden ausgegraut/deaktiviert. */
    /**
     * Schlüssel der bereits zugewiesenen Mails — je Link BEIDE Formen: die
     * Graph-`id` und die `internetMessageId` (siehe `zuweisungsSchluessel`).
     * Geprüft wird deshalb über {@link istZugewiesen}, nicht direkt per `.has`:
     * eine schon verknüpfte Mail darf auch dann nicht erneut angeboten werden,
     * wenn der Verlauf gerade ihre ZWEITE Ordnerkopie liefert (die trägt eine
     * andere Graph-`id`).
     */
    assignedMessageIds: Set<string>;
    onClose: () => void;
    onLinksChanged: () => void | Promise<void>;
};

type FlatFolder = { folder: MailFolder; depth: number };

/** Eine im Picker gewählte Nachricht trägt ihre Quell-Mailbox mit sich. */
type SelEntry = { mailbox: string; email: Email };

/** Eine Zeile der Liste — in beiden Modi über ihre Quell-Mailbox aufgelöst. */
type Row = { email: Email; mailbox: string; inSearch: boolean };

const PAGE_SIZE = 25;
const SEARCH_TOP = 25;

const flattenFolders = (tree: MailFolder[], depth = 0): FlatFolder[] => {
    const out: FlatFolder[] = [];
    for (const f of tree) {
        out.push({ folder: f, depth });
        if (f.children?.length) out.push(...flattenFolders(f.children, depth + 1));
    }
    return out;
};

const fmtDate = (d: Date): string =>
    !d || Number.isNaN(d.getTime()) || d.getTime() === 0
        ? ""
        : d.toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" });

const toIso = (d: Date): string | null =>
    d instanceof Date && !Number.isNaN(d.getTime()) && d.getTime() !== 0 ? d.toISOString() : null;

export const AddMailFromMailboxDialog: React.FC<Props> = ({
    vorgangId,
    todoId,
    contactId,
    assignedMessageIds,
    onClose,
    onLinksChanged,
}) => {
    const { mailboxes, ownMailbox, folderTrees } = useMailContext();

    // Nur lesbare Postfächer sind Ziel des Postfach-Dropdowns UND des Such-Fan-outs.
    const readableMailboxes = useMemo(() => mailboxes.filter(canReadMailbox), [mailboxes]);
    const readableAddrs = useMemo(
        () => readableMailboxes.map((m) => m.mailboxAddress),
        [readableMailboxes],
    );

    // ── Ordner-Lookups je Postfach (aus dem eager geladenen MailContext-Baum) ──
    // Namen (Chip) ohne Posteingang; Inbox-Ids für die Auto-Archiv-Entscheidung.
    const folderNameByMailbox = useMemo(() => {
        const out: Record<string, Map<string, string>> = {};
        for (const [mb, tree] of Object.entries(folderTrees)) out[mb] = buildFolderNameMap(tree);
        return out;
    }, [folderTrees]);
    const inboxIdsByMailbox = useMemo(() => {
        const out: Record<string, Set<string>> = {};
        for (const [mb, tree] of Object.entries(folderTrees)) out[mb] = collectInboxFolderIds(tree);
        return out;
    }, [folderTrees]);

    // ── Postfach/Ordner (Browse-Modus) ──
    const [mailbox, setMailbox] = useState<string>("");
    const [folders, setFolders] = useState<FlatFolder[]>([]);
    const [folderId, setFolderId] = useState<string>("");
    const [loadingFolders, setLoadingFolders] = useState(false);

    const selectedInboxId = useMemo(
        () => folders.find((f) => f.folder.wellKnownName === "inbox")?.folder.id ?? "",
        [folders],
    );

    // ── Suche ──
    const [search, setSearch] = useState("");
    const debouncedSearch = useDebouncedValue(search, 400);
    // Default-Suchvorschlag: participants: aus den Kontakt-Adressen (löschbar).
    // `null` = Adressen werden noch geladen (unterdrückt Modus-Flackern).
    const [contactAddrs, setContactAddrs] = useState<string[] | null>(null);
    // T6: Basis-Filter default AUS („Schriftwechsel-Filter raus") — der Picker
    // zeigt zunächst den GANZEN Ordner-Verlauf; der Kontakt-Filter ist per Chip
    // zuschaltbar.
    const [baseActive, setBaseActive] = useState(false);
    const participantsBase = useMemo(
        () => buildParticipantsQuery(contactAddrs ?? []),
        [contactAddrs],
    );
    const ready = contactAddrs !== null;

    // Effektive KQL: Kontakt-Basis UND getippter Teil (≥2 Zeichen), sonst je einzeln.
    const kql = useMemo(() => {
        const typed = debouncedSearch.trim();
        const typedPart = typed.length >= 2 ? typed : "";
        const base = baseActive ? participantsBase : "";
        if (typedPart && base) return `(${base}) AND (${typedPart})`;
        return typedPart || base;
    }, [debouncedSearch, baseActive, participantsBase]);
    const searchMode = kql.length > 0;

    // ── Browse-Liste (paginiert) ──
    const [browseMessages, setBrowseMessages] = useState<Email[]>([]);
    const [browseLoading, setBrowseLoading] = useState(false);
    const [browseLoadingMore, setBrowseLoadingMore] = useState(false);
    const [browseSkip, setBrowseSkip] = useState(0);
    const [browseHasMore, setBrowseHasMore] = useState(false);
    // Request-Token des Browse-Modus: die Erst-Lade-Wirkung erhöht ihn bei jedem
    // (Neu-)Start (Postfach-/Ordnerwechsel). „Mehr laden" hält den Token beim
    // Start fest und verwirft eine Seite, deren Token beim Auflösen veraltet ist
    // — sonst würden nachlaufende Treffer mit dem NEUEN Postfach etikettiert
    // (späterer Graph-404 beim Öffnen; Review #921).
    const browseReqRef = useRef(0);

    // ── Such-Liste (Fan-out) ──
    const [searchResults, setSearchResults] = useState<SidebarThreadHit[] | null>(null);
    const [searching, setSearching] = useState(false);

    // ── Auswahl ({mailbox, messageId}-Paare) ──
    const [selected, setSelected] = useState<Map<string, SelEntry>>(new Map());
    // T6: Auto-Archiv default AUS — das Verlinken lässt die Mail standardmäßig im
    // Posteingang; Archivieren ist bewusst per Checkbox zuschaltbar.
    const [archiveInbox, setArchiveInbox] = useState(false);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // ── Vorschau ({mailbox, messageId}) ──
    const [previewRef, setPreviewRef] = useState<{ mailbox: string; messageId: string } | null>(null);
    const [previewBody, setPreviewBody] = useState<Email | null>(null);
    const [previewLoading, setPreviewLoading] = useState(false);
    const [threadBusy, setThreadBusy] = useState(false);
    const previewReqRef = useRef(0);

    const resetPreview = useCallback(() => {
        previewReqRef.current++;
        setPreviewRef(null);
        setPreviewBody(null);
        setPreviewLoading(false);
    }, []);

    /**
     * Liegt eine Nachricht (mailbox + parentFolderId) im Posteingang ihres
     * Postfachs? Nur dann darf das Auto-Archiv greifen. Fallback für das gerade
     * selbst geladene Postfach (Race gegen den eager MailContext-Baum) über die
     * lokal ermittelte Inbox-Id. Ohne auflösbaren Ordner konservativ `false`.
     */
    const computeIsInInbox = useCallback(
        (mb: string, email: Email): boolean => {
            const pid = email.parentFolderId;
            if (!pid) return false;
            if (inboxIdsByMailbox[mb]?.has(pid)) return true;
            if (mb === mailbox && selectedInboxId && pid === selectedInboxId) return true;
            return false;
        },
        [inboxIdsByMailbox, mailbox, selectedInboxId],
    );

    // 1) Kontakt-Adressen für den Default-Vorschlag laden (einmalig je Kontakt).
    useEffect(() => {
        let cancelled = false;
        setContactAddrs(null);
        void (async () => {
            if (contactId == null) {
                if (!cancelled) setContactAddrs([]);
                return;
            }
            const addrs = await graphMailService.fetchContactMailAddresses(contactId).catch(() => [] as string[]);
            if (!cancelled) setContactAddrs(addrs);
        })();
        return () => {
            cancelled = true;
        };
    }, [contactId]);

    // 2) Default-Postfach: eigenes (isOwn/ownMailbox), sonst erstes lesbare.
    useEffect(() => {
        if (mailbox || readableMailboxes.length === 0) return;
        const own =
            readableMailboxes.find((b) => b.isOwn) ??
            readableMailboxes.find((b) => b.mailboxAddress === ownMailbox) ??
            readableMailboxes[0];
        setMailbox(own?.mailboxAddress ?? "");
    }, [mailbox, readableMailboxes, ownMailbox]);

    // 3) Ordnerbaum des gewählten Postfachs → Ordner-Dropdown + Default = Inbox.
    //    Über den cached fetchFolderTree (teilt sich den Cache mit MailContext).
    useEffect(() => {
        if (!mailbox) return;
        let cancelled = false;
        void (async () => {
            setLoadingFolders(true);
            try {
                const tree = await graphMailService.fetchFolderTree(mailbox);
                if (cancelled) return;
                const flat = flattenFolders(tree);
                setFolders(flat);
                const inbox = flat.find((f) => f.folder.wellKnownName === "inbox") ?? flat[0];
                setFolderId(inbox?.folder.id ?? "");
            } catch (e: any) {
                if (!cancelled) {
                    setFolders([]);
                    setFolderId("");
                    setError(e?.message ?? "Ordner konnten nicht geladen werden.");
                }
            } finally {
                if (!cancelled) setLoadingFolders(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [mailbox]);

    // 4) Browse-Liste: erste Seite laden, sobald Postfach/Ordner stehen (nur im
    //    Browse-Modus). Postfachwechsel schließt die Vorschau.
    useEffect(() => {
        if (!ready || searchMode || !mailbox || !folderId) return;
        // Neustart des Browse-Modus → Token bumpen (invalidiert in-flight „Mehr laden").
        browseReqRef.current++;
        let cancelled = false;
        void (async () => {
            setBrowseLoading(true);
            setBrowseMessages([]);
            setBrowseSkip(0);
            setBrowseHasMore(false);
            try {
                const page = await graphMailService.fetchFolderMessagesPage(mailbox, folderId, 0, PAGE_SIZE);
                if (cancelled) return;
                setBrowseMessages(page);
                setBrowseSkip(page.length);
                setBrowseHasMore(page.length >= PAGE_SIZE);
            } catch (e: any) {
                if (!cancelled) {
                    setBrowseMessages([]);
                    setError(e?.message ?? "Mails konnten nicht geladen werden.");
                }
            } finally {
                if (!cancelled) setBrowseLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [ready, searchMode, mailbox, folderId]);

    const loadMoreBrowse = useCallback(async () => {
        if (browseLoadingMore || !browseHasMore || !mailbox || !folderId) return;
        // Token zum Aufrufzeitpunkt festhalten (Postfach/Ordner gültig für DIESE Seite).
        const req = browseReqRef.current;
        setBrowseLoadingMore(true);
        try {
            const page = await graphMailService.fetchFolderMessagesPage(mailbox, folderId, browseSkip, PAGE_SIZE);
            // Postfach/Ordner seit Start gewechselt? → Seite verwerfen (keine Fremd-Etikettierung).
            if (browseReqRef.current !== req) return;
            setBrowseMessages((prev) => {
                const seen = new Set(prev.map((m) => m.id));
                return [...prev, ...page.filter((m) => !seen.has(m.id))];
            });
            setBrowseSkip((s) => s + page.length);
            setBrowseHasMore(page.length >= PAGE_SIZE);
        } catch (e: any) {
            if (browseReqRef.current === req) setError(e?.message ?? "Weitere Mails konnten nicht geladen werden.");
        } finally {
            setBrowseLoadingMore(false);
        }
    }, [browseLoadingMore, browseHasMore, mailbox, folderId, browseSkip]);

    // 5) Such-Fan-out über alle lesbaren Postfächer (Fehler je Postfach schlucken).
    useEffect(() => {
        if (!ready) return;
        if (!searchMode) {
            setSearchResults(null);
            setSearching(false);
            return;
        }
        if (readableAddrs.length === 0) {
            setSearchResults([]);
            return;
        }
        let cancelled = false;
        setSearching(true);
        void (async () => {
            try {
                const settled = await Promise.allSettled(
                    readableAddrs.map(async (mb) => {
                        const items = await graphMailService.searchMail(mb, kql, SEARCH_TOP);
                        return items.map((e) => ({ ...e, srcMailbox: mb }) as SidebarThreadHit);
                    }),
                );
                if (cancelled) return;
                const hits = settled.flatMap((r) => (r.status === "fulfilled" ? r.value : []));
                setSearchResults(mergeThreadHits(hits));
            } finally {
                if (!cancelled) setSearching(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [ready, searchMode, kql, readableAddrs]);

    // Escape schließt (außer während des Speicherns).
    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            if (e.key === "Escape" && !busy) onClose();
        };
        alleDokumente.addEventListener("keydown", onKey);
        return () => alleDokumente.removeEventListener("keydown", onKey);
    }, [busy, onClose]);

    const toggle = useCallback((mb: string, email: Email) => {
        setSelected((prev) => {
            const next = new Map(prev);
            if (next.has(email.id)) next.delete(email.id);
            else next.set(email.id, { mailbox: mb, email });
            return next;
        });
    }, []);

    /**
     * Hängt diese Nachricht bereits am Ziel? Geprüft wird die Graph-`id` UND
     * die `internetMessageId` — sonst böte der Picker die Sendekopie einer
     * Mail erneut an, deren zugestellte Kopie längst verknüpft ist.
     */
    const istZugewiesen = useCallback(
        (m: { id: string; internetMessageId?: string | null }) => {
            if (assignedMessageIds.has(m.id)) return true;
            const imid = (m.internetMessageId ?? "").trim();
            return imid.length > 0 && assignedMessageIds.has(imid);
        },
        [assignedMessageIds],
    );

    const selectOne = useCallback(
        (mb: string, email: Email) => {
            if (istZugewiesen(email)) return;
            setSelected((prev) => new Map(prev).set(email.id, { mailbox: mb, email }));
        },
        [istZugewiesen],
    );

    const openPreview = useCallback((mb: string, m: Email) => {
        const req = ++previewReqRef.current;
        setPreviewRef({ mailbox: mb, messageId: m.id });
        setPreviewBody(null);
        setPreviewLoading(true);
        void graphMailService
            .fetchMessageBody(mb, m.id)
            .then((full) => {
                if (previewReqRef.current === req) setPreviewBody(full);
            })
            .catch(() => {
                // Kein Voll-Body → Listen-Objekt als Fallback (Vorschau bleibt nutzbar).
                if (previewReqRef.current === req) setPreviewBody(m);
            })
            .finally(() => {
                if (previewReqRef.current === req) setPreviewLoading(false);
            });
    }, []);

    const selectThread = useCallback(
        async (mb: string, conversationId: string) => {
            if (!conversationId || threadBusy) return;
            setThreadBusy(true);
            try {
                const thread = await graphMailService.fetchConversation(mb, conversationId);
                setSelected((prev) => {
                    const next = new Map(prev);
                    // Der Verlauf kommt bewusst POSTFACHWEIT über alle Ordner
                    // (damit die eigenen Antworten aus „Gesendet" mitkommen) —
                    // eine selbst gesendete Mail liegt darin deshalb ZWEIMAL:
                    // als Sendekopie und als zugestellte Kopie, mit
                    // verschiedener Graph-`id`, aber gleicher
                    // `internetMessageId`. Ohne diese Entdopplung wanderten
                    // beide in die Auswahl und der Vorgang bekäme zwei Zeilen
                    // für eine Mail.
                    const ident = (box: string, e: Email) =>
                        mailIdentitaet({ ...e, mailbox: box, messageId: e.id });
                    // Identität -> bereits gewählte Graph-Id, damit eine zweite
                    // Kopie die erste ERSETZEN kann statt nur durchzufallen.
                    const gewaehlt = new Map<string, string>();
                    for (const e of next.values()) gewaehlt.set(ident(e.mailbox, e.email), e.email.id);

                    for (const t of thread) {
                        if (istZugewiesen(t)) continue;
                        const schluessel = ident(mb, t);
                        const bisher = gewaehlt.get(schluessel);
                        if (bisher !== undefined) {
                            // Von zwei Kopien gewinnt die im POSTEINGANG: nur sie
                            // darf beim Zuweisen archiviert werden. Behielte man
                            // die Sendekopie (sie steht im Verlauf zuerst), bliebe
                            // die zugestellte unarchiviert liegen.
                            const alt = next.get(bisher);
                            if (!computeIsInInbox(mb, t)) continue;
                            if (alt && computeIsInInbox(alt.mailbox, alt.email)) continue;
                            next.delete(bisher);
                        }
                        gewaehlt.set(schluessel, t.id);
                        next.set(t.id, { mailbox: mb, email: t });
                    }
                    return next;
                });
            } catch (e: any) {
                setError(e?.message ?? "Verlauf konnte nicht geladen werden.");
            } finally {
                setThreadBusy(false);
            }
        },
        [threadBusy, istZugewiesen, computeIsInInbox],
    );

    const anyInboxSelected = useMemo(
        () => [...selected.values()].some((e) => computeIsInInbox(e.mailbox, e.email)),
        [selected, computeIsInInbox],
    );

    const submit = async () => {
        if (busy || selected.size === 0) return;
        setBusy(true);
        setError(null);
        try {
            const items: MailLinkBatchItemInput[] = [...selected.values()].map(({ mailbox: mb, email }) => ({
                mailbox: mb,
                messageId: email.id,
                conversationId: email.conversationId || null,
                // Ohne sie hielte der Server die Sendekopie und die zugestellte
                // Kopie derselben Mail für zwei Mails.
                internetMessageId: email.internetMessageId || null,
                subject: email.subject || null,
                senderName: email.senderName || null,
                senderEmail: email.senderEmail || null,
                // „An"-Snapshot (T6): Anzeige-String der To-Empfänger.
                recipients: (email.to ?? []).map((a) => a.name || a.address).filter(Boolean).join(", ") || null,
                receivedAt: toIso(email.date),
                preview: email.preview || null,
                isInInbox: computeIsInInbox(mb, email),
            }));

            const willArchive = archiveInbox && items.some((i) => i.isInInbox);
            const { results } = await mailLinkService.createBatch({
                ...(vorgangId != null ? { vorgangId } : {}),
                ...(todoId != null ? { todoId } : {}),
                archiveInbox,
                items,
            });
            if (willArchive) {
                // Nur die Postfächer verwerfen, aus deren Posteingang archiviert wurde.
                const archivedMailboxes = new Set(items.filter((i) => i.isInInbox).map((i) => i.mailbox));
                for (const mb of archivedMailboxes) graphMailService.invalidateMailbox(mb);
            }
            await onLinksChanged();
            const failed = results.filter((r) => r.error);
            if (failed.length > 0) {
                setError(`${failed.length} von ${results.length} Mails konnten nicht zugeordnet werden.`);
                setBusy(false);
                return;
            }
            const ungesichert = results.filter(r => r.mailLinkId && r.archive && r.archive.state !== "stored");
            if (ungesichert.length > 0) {
                void hinweisen({ titel: "Sicherung noch nicht abgeschlossen", text: `${ungesichert.length} Mail(s) sind verknüpft, aber noch nicht gesichert. Den Status sehen Sie im Vorgang.` });
            }
            const archiveErrors = results.filter((r) => r.archiveError);
            if (archiveErrors.length > 0) {
                void hinweisen({
                    titel: "Ordneraktion nicht abgeschlossen",
                    text: `${archiveErrors.length} Mail(s) sind verknüpft, konnten aber nicht in den Outlook-Archivordner verschoben werden. Der Sicherungsstatus wird separat im Vorgang angezeigt.`,
                });
            }
            onClose();
        } catch (e: any) {
            setError(e?.message ?? "Zuordnung fehlgeschlagen.");
            setBusy(false);
        }
    };

    // Zeilen des aktiven Modus.
    const rows: Row[] = searchMode
        ? (searchResults ?? []).map((h) => ({ email: h, mailbox: h.srcMailbox, inSearch: true }))
        : browseMessages.map((m) => ({ email: m, mailbox, inSearch: false }));
    const listLoading = searchMode ? searching : browseLoading;

    const previewSelected = previewBody ? selected.has(previewBody.id) : false;
    const previewAssigned = previewBody ? istZugewiesen(previewBody) : false;

    return createPortal(
        <div
            className="vgd vgm-modal-backdrop"
            onMouseDown={(e) => {
                if (e.target === e.currentTarget && !busy) onClose();
            }}
        >
            <div className="vgm-modal" role="dialog" aria-modal="true" aria-label="Mail hinzufügen">
                <div className="vgm-modal-head">
                    <span style={{ fontWeight: 700 }}>Mail hinzufügen</span>
                    <button
                        type="button"
                        className="vgm-modal-x"
                        onClick={() => !busy && onClose()}
                        aria-label="Schließen"
                    >
                        ×
                    </button>
                </div>

                <div className="vgm-modal-controls">
                    <label className="vgm-modal-field">
                        <span>Postfach</span>
                        <select
                            value={mailbox}
                            disabled={busy || readableMailboxes.length === 0}
                            onChange={(e) => setMailbox(e.target.value)}
                        >
                            {readableMailboxes.map((b) => (
                                <option key={b.mailboxAddress} value={b.mailboxAddress}>
                                    {(b.displayName ? `${b.displayName} · ${b.mailboxAddress}` : b.mailboxAddress) +
                                        (b.isOwn ? " (eigenes)" : "")}
                                </option>
                            ))}
                        </select>
                    </label>
                    <label className="vgm-modal-field">
                        <span>Ordner</span>
                        <select
                            value={folderId}
                            disabled={loadingFolders || busy || folders.length === 0}
                            onChange={(e) => setFolderId(e.target.value)}
                        >
                            {folders.map(({ folder, depth }) => (
                                <option key={folder.id} value={folder.id}>
                                    {`${"  ".repeat(depth)}${folder.displayName}`}
                                </option>
                            ))}
                        </select>
                    </label>
                </div>

                <div className="vgm-modal-searchrow">
                    <MailSearchInput
                        value={search}
                        onChange={setSearch}
                        busy={searchMode && searching}
                        placeholder="In allen Postfächern suchen …"
                        className="vgm-modal-search"
                        onKeyDown={(e) => {
                            // Escape bei nicht-leerem Suchtext: nur die Suche leeren
                            // (MailSearchInput selbst) und den Dialog OFFEN lassen —
                            // stopPropagation hält den Escape vom Dialog-Schließen ab
                            // (Review #921). Bei leerem Text propagiert Escape normal.
                            if (e.key === "Escape" && search.length > 0) e.stopPropagation();
                        }}
                    />
                    {participantsBase && (
                        <button
                            type="button"
                            className={baseActive ? "vgm-basefilter" : "vgm-basefilter off"}
                            title={
                                baseActive
                                    ? "Nur Schriftwechsel dieses Kontakts — klicken zum Aufheben"
                                    : "Auf den Schriftwechsel dieses Kontakts einschränken"
                            }
                            onClick={() => setBaseActive((v) => !v)}
                        >
                            <span>
                                {baseActive ? "Nur Schriftwechsel des Kontakts" : "Auf Kontakt-Schriftwechsel einschränken"}
                            </span>
                            <span aria-hidden className="vgm-basefilter-x">
                                {baseActive ? "×" : "+"}
                            </span>
                        </button>
                    )}
                </div>

                <div className="vgm-modal-body">
                    <div className="vgm-modal-list">
                        {!ready || listLoading ? (
                            <div className="vgd-drop-empty">Mails werden geladen…</div>
                        ) : rows.length === 0 ? (
                            <div className="vgd-drop-empty">
                                {searchMode ? "Keine passenden Mails gefunden." : "Keine Mails in diesem Ordner."}
                            </div>
                        ) : (
                            <>
                                {rows.map(({ email: m, mailbox: mb, inSearch }) => {
                                    const already = istZugewiesen(m);
                                    const checked = selected.has(m.id);
                                    const active = previewRef?.messageId === m.id;
                                    const folderName =
                                        inSearch && m.parentFolderId
                                            ? folderNameByMailbox[mb]?.get(m.parentFolderId)
                                            : undefined;
                                    return (
                                        <div
                                            key={`${mb}:${m.id}`}
                                            className={`vgm-msg-row${already ? " is-disabled" : ""}${
                                                active ? " is-active" : ""
                                            }`}
                                        >
                                            <input
                                                type="checkbox"
                                                checked={checked && !already}
                                                disabled={already || busy}
                                                onChange={() => toggle(mb, m)}
                                                onClick={(e) => e.stopPropagation()}
                                                aria-label="Mail auswählen"
                                            />
                                            <span
                                                className="vgm-msg-main"
                                                role="button"
                                                tabIndex={0}
                                                onClick={() => openPreview(mb, m)}
                                                onKeyDown={(e) => {
                                                    if (e.key === "Enter" || e.key === " ") {
                                                        e.preventDefault();
                                                        openPreview(mb, m);
                                                    }
                                                }}
                                            >
                                                <span className="vgm-msg-subject">{m.subject || "(Kein Betreff)"}</span>
                                                <span className="vgm-msg-meta">
                                                    {[m.senderName || m.senderEmail, fmtDate(m.date)]
                                                        .filter(Boolean)
                                                        .join(" · ")}
                                                </span>
                                            </span>
                                            {inSearch && (
                                                <span className="vgm-row-chips">
                                                    <span className="vgm-row-chip">{mailboxChipLabel(mb)}</span>
                                                    {folderName && (
                                                        <span className="vgm-row-folderchip" title={folderName}>
                                                            {folderName}
                                                        </span>
                                                    )}
                                                </span>
                                            )}
                                            {already && <span className="vgd-minibadge gold">zugeordnet</span>}
                                        </div>
                                    );
                                })}
                                {!searchMode && browseHasMore && (
                                    <div className="vgm-loadmore">
                                        <button
                                            type="button"
                                            className="vgd-btn small"
                                            disabled={browseLoadingMore || busy}
                                            onClick={() => void loadMoreBrowse()}
                                        >
                                            {browseLoadingMore ? "Wird geladen…" : "Mehr laden"}
                                        </button>
                                    </div>
                                )}
                            </>
                        )}
                    </div>

                    <div className="vgm-modal-preview">
                        {previewRef ? (
                            <>
                                <div className="vgm-preview-head">
                                    <div className="vgm-preview-actions">
                                        <button
                                            type="button"
                                            className="vgd-btn small"
                                            disabled={busy || previewAssigned || previewSelected || !previewBody}
                                            onClick={() =>
                                                previewBody && selectOne(previewRef.mailbox, previewBody)
                                            }
                                        >
                                            {previewSelected ? "Ausgewählt" : "Nur diese Mail"}
                                        </button>
                                        <button
                                            type="button"
                                            className="vgd-btn small"
                                            disabled={busy || threadBusy || !previewBody?.conversationId}
                                            title={previewBody?.conversationId ? "" : "Kein Verlauf vorhanden"}
                                            onClick={() =>
                                                previewBody?.conversationId &&
                                                void selectThread(previewRef.mailbox, previewBody.conversationId)
                                            }
                                        >
                                            {threadBusy ? "Verlauf lädt…" : "Ganzer Verlauf"}
                                        </button>
                                    </div>
                                    <button
                                        type="button"
                                        className="vgm-modal-x"
                                        onClick={resetPreview}
                                        aria-label="Vorschau schließen"
                                    >
                                        ×
                                    </button>
                                </div>
                                <div className="vgm-preview-subject">{previewBody?.subject || "(Kein Betreff)"}</div>
                                <div className="vgm-preview-meta">
                                    {previewBody
                                        ? [previewBody.senderName || previewBody.senderEmail, fmtDate(previewBody.date)]
                                              .filter(Boolean)
                                              .join(" · ")
                                        : ""}
                                </div>
                                <div className="vgm-preview-body">
                                    {previewLoading ? (
                                        <div className="vgd-drop-empty">Vorschau lädt…</div>
                                    ) : (
                                        <SandboxedMailBody html={previewBody?.body || ""} blockRemote minHeight={80} />
                                    )}
                                </div>
                            </>
                        ) : (
                            <div className="vgm-preview-placeholder">Mail auswählen…</div>
                        )}
                    </div>
                </div>

                {error && <div className="vgm-modal-error">{error}</div>}

                <div className="vgm-modal-foot">
                    <label
                        className={`vgm-archive-toggle${anyInboxSelected ? "" : " is-off"}`}
                        title={anyInboxSelected ? "" : "Nur für Nachrichten aus dem Posteingang relevant"}
                    >
                        <input
                            type="checkbox"
                            checked={archiveInbox && anyInboxSelected}
                            disabled={!anyInboxSelected || busy}
                            onChange={(e) => setArchiveInbox(e.target.checked)}
                        />
                        <span>Aus Posteingang nach dem Hinzufügen archivieren</span>
                    </label>
                    <div className="vgm-foot-actions">
                        <button type="button" className="vgd-btn" onClick={() => !busy && onClose()} disabled={busy}>
                            Abbrechen
                        </button>
                        <button
                            type="button"
                            className="vgd-btn primary"
                            onClick={submit}
                            disabled={busy || selected.size === 0}
                        >
                            {busy
                                ? "Wird hinzugefügt…"
                                : `${selected.size} Mail${selected.size === 1 ? "" : "s"} hinzufügen`}
                        </button>
                    </div>
                </div>
            </div>
        </div>,
        document.body,
    );
};
